
import { createNamespacedHelpers } from 'vuex';
import { GET_LISTING_DATA, GET_LISTING_FILTERS_DATA } from '~/api/apolloListingQueries';
import { GET_POPULAR_COLLECTIONS, GET_BRAND_DATA, GET_COLLECTION_DATA } from '~/api/apolloBrandQueries';
import routeMixin from '~/mixins/routeMixin';
import respUtility from '~/mixins/respUtility';
import { htmlspecialcharsDecode } from '@/mixins/helpers';

const { mapState, mapGetters, mapMutations } = createNamespacedHelpers('listing');

export default {
  name: 'BrandPage',
  mixins: [routeMixin, respUtility],
  data: () => ({
    loadingFilters: 0,
    loadingProducts: 0,
    loadingPopularCollections: 0,
    loadingBrandData: 0,
    catalogSearch: null,
    filtersBlock: null,
    count: 0,
    priceData: null,
    currentFiltersBlock: null,
    minPrice: 0,
    maxPrice: 0,
    isSSR: true,
    popularCollections: [],
    popularCollectionsAll: 0,
    brandRating: {},
    brandTopBanner: {},
    additionalData: null,
    isViewContentTracked: false,
  }),
  head() {
    return {
      script: [
        {
          src: 'https://static.criteo.net/js/ld/ld.js',
          async: true,
          skip: !this.$config.criteo.criteoDefaultEnable,
        },
        {
          src: '/js/product-event-tracking.handler.js',
          async: true,
          skip: !this.$config.productEventTracking.enableTrackingEvent,
        },
      ],
    };
  },
  async fetch() {
    if (this.isBrandPage) {
      this.getBrandData();
      this.getPopularCollections();
    }
    if (this.isCollectionPage) {
      this.getCollectionData();
    }
    await this.getListingData();
  },
  computed: {
    ...mapGetters(['defaultParams']),
    ...mapState(['searchParams', 'currentSelected']),
    breadcrumbs() {
      const breadcrumbs = [...(this.$store.state.metaData?.addons?.breadcrumbs || [])];
      return breadcrumbs.map((breadcrumb, index) => {
        if (index === 0 || index === 1 || (index === 2 && this.isCollectionPage)) {
          return { ...breadcrumb, isInternal: true };
        }
        return breadcrumb;
      });
    },
    footerData() {
      return this.$store.state.metaData?.addons;
    },
    loading() {
      return {
        filters: this.loadingFilters,
        products: this.loadingProducts,
        brandData: this.loadingBrandData,
        popularCollections: this.loadingPopularCollections,
      };
    },
    noResults() {
      return this.resultsData && this.resultsData.itemsCount === 0;
    },
    resultsData() {
      return this.catalogSearch || {};
    },
    filtersFromRoute() {
      const data = this.additionalData?.facets || [];
      const extensionData = this.additionalData?.extensionParams || [];
      if (!(data && data.length) && !(extensionData && extensionData.length)) {
        return null;
      }
      const selected = {};
      data.forEach((f) => {
        if (!(Array.isArray(f.value) && f.value.length)) {
          return;
        }
        selected[f.attributeCode] = f.value;
      });
      extensionData.forEach((f) => {
        if (!(f.value && f.value.length)) {
          return;
        }
        selected[f.key] = [{ attrValue: f.value }];
      });
      const filters = this.filtersBlock || [];
      const result = {
        selected,
        customPrice: null,
      };
      if (selected.price) {
        const block = filters.find((b) => b.attrCode === 'price');
        if (!Array.isArray(block?.facets)) {
          return null;
        }
        selected.price.forEach((facet) => {
          const item = block.facets.find((f) => f.attrValue === facet.attrValue);
          if (item) {
            return;
          }
          result.customPrice = facet;
        });
      }
      if (result.customPrice) {
        delete result.selected.price;
      }
      return result;
    },
    extraChips() {
      const extraChips = this.additionalData?.extraChips || [];
      return extraChips;
    },
    routePath() {
      return this.$route.path;
    },
  },
  watch: {
    searchParams(params, old) {
      if (JSON.stringify(params) !== JSON.stringify(old)) {
        this.getListingData(params);
      }
    },
    currentSelected(newSelected) {
      if (this.$device.isMobileOrTablet || this.isTouchDevice) {
        if (this.filtersFromRoute !== newSelected) {
          this.$nextTick(this.getFilters);
        } else if (this.filtersBlock !== this.currentFiltersBlock) {
          this.currentFiltersBlock = this.filtersBlock;
        }
        return;
      }
      const isChanged = this.checkFiltersSelectedChanged(newSelected, this.filtersFromRoute);
      if (isChanged) {
        this.changeRouteFacets(newSelected);
        return;
      }
      if (this.$store.state.listing.extensionFilters) {
        this.changeRouteFacets(this.currentSelected);
      }
    },
    currentFiltersBlock(newBlocks) {
      if (!this.count && this.filtersBlock === newBlocks) {
        return;
      }
      if (!(this.currentSelected && this.currentSelected.selected)) {
        return;
      }
      const copy = {};
      let isChanged = false;
      Object.keys(this.currentSelected.selected).forEach((attrCode) => {
        if (!(Array.isArray(this.currentSelected.selected[attrCode])
            && this.currentSelected.selected[attrCode].length)) {
          return;
        }
        const currentBlock = newBlocks.find((block) => block.attrCode === attrCode);
        if (!currentBlock) {
          isChanged = true;
          return;
        }
        copy[attrCode] = this.currentSelected.selected[attrCode].reduce((acc, item) => {
          const currentItem = currentBlock.facets.find((i) => {
            if (i.attrValue === item.attrValue) {
              return i;
            }
            if (i.child) {
              const currentChild = i.child.find((j) => j.attrValue === item.attrValue);
              return currentChild;
            }
            return false;
          });
          if (currentItem && Boolean(currentItem.itemCount)) {
            acc.push(item);
          }
          return acc;
        }, []);
        if (copy[attrCode].length !== this.currentSelected.selected[attrCode].length) {
          isChanged = true;
        }
      });
      if (isChanged) {
        this.setSelected(copy);
      }
    },
  },
  mounted() {
    // checking extension filters
    window.addEventListener('message', this.handleExtensionFilters, false);
    if (this.isSSR && this.catalogSearch) {
      this.setIntegrationsEvents();
    }
  },
  beforeRouteUpdate(to, from, next) {
    this.isViewContentTracked = false;
    next();
  },
  beforeDestroy() {
    // remove checking extension filters
    window.removeEventListener('message', this.handleExtensionFilters, false);
  },
  methods: {
    ...mapMutations([
      'setCurrentSelected',
      'setSelected',
    ]),
    setIntegrationsEvents() {
      if (window.gtag) {
        this.viewItemListTracking(this.catalogSearch?.items);
      } else {
        window.addEventListener('gtm_loaded', () => this.viewItemListTracking(this.catalogSearch?.items), { once: true });
      }
      this.pushCriteoEvents(this.catalogSearch?.items);
      if (this.$store.state.pageData.initial) {
        return;
      }
      const title = `${htmlspecialcharsDecode(this.catalogSearch?.header)} | 1StopBedrooms`;
      if (this.catalogSearch?.header && title !== window.document.title) {
        window.document.title = title;
      }
    },
    async getListingData(p) {
      this.loadingFilters += 1;
      this.loadingProducts += 1;
      const params = { ...(p || this.searchParams) };
      const zipValue = !process.server ? window.localStorage.getItem('zipcode') : null;
      const defaultParams = {
        slug: this.$route.path[0] === '/' ? this.$route.path.slice(1) : this.$route.path,
        request: {
          ...this.defaultParams,
          ...params,
          facet: this.formatFacetsForQuery(params.facet),
        },
        zipcode: zipValue,
      };
      if (Object.prototype.hasOwnProperty.call(defaultParams.request, 'text')) {
        delete defaultParams.request.text;
      }
      const f1 = async () => {
        const { listing: { listingCategory } } = await this.$graphql.default
          .request(GET_LISTING_DATA, defaultParams) || {};
        this.catalogSearch = listingCategory;
        if (this.catalogSearch?.pages < defaultParams.request.page && defaultParams.request.page > 1) {
          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              page: this.catalogSearch?.pages,
            },
          });
          return;
        }
        this.isSSR = process.server;
        if (!this.isSSR) {
          this.setIntegrationsEvents();
        }
        this.$taskManager.append(() => {
          if (!this.isViewContentTracked) {
            this.isViewContentTracked = true;
          }
        });
      };
      const f2 = async () => {
        const { listing: { listingCategory } } = await this.$graphql.default
          .request(GET_LISTING_FILTERS_DATA, defaultParams) || {};
        this.count = listingCategory.itemsCount;
        this.filtersBlock = listingCategory.filtersBlock;
        this.minPrice = listingCategory.minPrice;
        this.maxPrice = listingCategory.maxPrice;
        this.additionalData = listingCategory.additionalData;
        if (!this.count) {
          this.resetFilters();
        }
      };

      try {
        await Promise.allSettled([
          f1(), f2(),
        ]);
      } catch (e) {
        console.error(e);
      }
      if (!this.count && this.isCollectionPage) {
        this.$router.push(this.brandTopBanner.brandSlug);
        return;
      }
      this.loadingFilters -= 1;
      this.loadingProducts -= 1;
      this.$store.commit('setGenuineSlug', this.additionalData?.genuineSlug);
      if (!this.catalogSearch?.itemsCount && this.filtersFromRoute?.customPrice) {
        this.changeRouteFacets({
          selected: this.filtersFromRoute?.selected || {},
          customPrice: null,
        });
      } else {
        this.$nextTick(() => {
          this.checkCurrentSelected();
          this.currentFiltersBlock = this.filtersBlock;
        });
      }
    },
    async getPopularCollections() {
      this.loadingPopularCollections += 1;
      const defaultParams = {
        slug: this.$route.path[0] === '/' ? this.$route.path.slice(1) : this.$route.path,
      };
      try {
        const { brand: { popularCollections } } = await this.$graphql.default
          .request(GET_POPULAR_COLLECTIONS, defaultParams) || {};
        this.popularCollections = popularCollections.list;
        this.popularCollectionsAll = popularCollections.total;
        this.$nextTick(() => {
          if (!this.isViewContentTracked) {
            this.isViewContentTracked = true;
          }
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingPopularCollections -= 1;
      }
    },
    async getBrandData() {
      this.loadingBrandData += 1;
      const defaultParams = {
        slug: this.$route.path[0] === '/' ? this.$route.path.slice(1) : this.$route.path,
      };
      const brandData = async () => {
        const { brand } = await this.$graphql.default
          .request(GET_BRAND_DATA, defaultParams) || {};
        this.brandRating = brand.rating || {};
        this.brandTopBanner = brand.topBanner || {};
      };
      try {
        await brandData();
      } catch (e) {
        console.error(e);
      }
      this.loadingBrandData -= 1;
    },
    async getCollectionData() {
      this.loadingBrandData += 1;
      const defaultParams = {
        slug: this.$route.path[0] === '/' ? this.$route.path.slice(1) : this.$route.path,
      };
      const collectionData = async () => {
        const { brandCollection } = await this.$graphql.default
          .request(GET_COLLECTION_DATA, defaultParams) || {};
        this.brandTopBanner = brandCollection.topBanner || {};
      };
      try {
        await collectionData();
      } catch (e) {
        console.error(e);
      }
      this.loadingBrandData -= 1;
    },
    checkCurrentSelected() {
      const data = this.filtersFromRoute || {
        selected: {},
        customPrice: null,
      };
      const isChanged = this.checkFiltersSelectedChanged(data, this.currentSelected);
      if (isChanged) {
        this.setCurrentSelected(data);
        return;
      }

      if (this.$store.state.extensionFilters) {
        this.changeRouteFacets(this.currentSelected);
      }
    },
    formatFacetsForQuery(selectedObj) {
      if (!selectedObj) {
        return [];
      }
      return Object.keys(selectedObj).map((key) => {
        const values = selectedObj[key].map((v) => (typeof v === 'string'
          ? v.replace('"', '\\"') : v));
        return {
          attributeCode: key,
          value: values,
        };
      });
    },
    async getFilters() {
      this.loadingFilters += 1;
      const defaultParams = {
        slug: this.$route.path[0] === '/' ? this.$route.path.slice(1) : this.$route.path,
      };
      defaultParams.request = {
        ...this.defaultParams,
        facet: this.formatFacetsForQuery(this.formatFacets(this.currentSelected)),
      };
      const { listing: { listingCategory } } = await this.$graphql.default
        .request(GET_LISTING_FILTERS_DATA, defaultParams) || {};
      this.count = listingCategory.itemsCount;
      if (listingCategory.filtersBlock?.length) {
        this.currentFiltersBlock = listingCategory.filtersBlock;
      }
      this.minPrice = listingCategory.minPrice;
      this.maxPrice = listingCategory.maxPrice;
      this.loadingFilters -= 1;
    },
    inputPrice(data) {
      this.priceData = { ...data };
    },
    handleExtensionFilters(event) {
      if (event.data && event.data.type === 'initExtensionFilters') {
        const { filters } = event.data;
        this.$store.commit('listing/setExtensionFilters', filters);
      }

      if (event.data && event.data.type === 'applyExtensionFilters') {
        const { filters } = event.data;
        this.$store.commit('listing/setExtensionFilters', filters);
        this.changeRouteFacets(this.currentSelected);
      }

      if (event.data && event.data.type === 'applyExtensionPrice') {
        const { price } = event.data;
        const { slug } = event.data;
        const selected = {
          ...this.$store.state.currentSelected,
          customPrice: { ...price },
        };
        this.changeRouteFacets(selected, slug);
      }
    },
    pushCriteoEvents(items) {
      if (!(window && this.$config.criteo.criteoDefaultEnable && Array.isArray(items))) {
        return;
      }
      // eslint-disable-next-line no-nested-ternary
      const deviceType = /iPad/.test(navigator.userAgent) ? 't'
        : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Opera Mini|Silk/.test(navigator.userAgent) ? 'm' : 'd';
      const ids = (items || []).filter((item) => item?.useInCriteoFeed || false).map((item) => item.webId);
      if (!ids.length) {
        return;
      }
      window.criteo_q = window.criteo_q || [];
      window.criteo_q.push(
        { event: 'setAccount', account: this.$config.criteo.criteoDefaultAccountId },
        { event: 'setSiteType', type: deviceType },
        { event: 'setEmail', email: this.$store.state.customer?.me?.email },
        { event: 'viewList', item: ids },
      );
    },
    viewItemListTracking(items) {
      const sum = items.reduce((acc, item) => acc + Number(item.price.finalPrice), 0);
      const itemsTracking = items.reduce((acc, curr) => { acc.push({ id: curr.webId, google_business_vertical: 'retail' }); return acc; }, []);
      window.gtag('event', 'view_item_list', {
        value: String(sum.toFixed(2)),
        items: itemsTracking,
      });
      const itemsRelatedProducts = items.reduce((acc, curr) => {
        const item = {
          affiliation: 'Google Merchandise Store',
          id: curr.webId,
          coupon: curr.couponData?.code || '',
          discount: `${curr.couponData?.discount}` || '',
          index: null,
          item_brand: curr.brand?.name,
          item_id: curr.webId,
          item_list_id: 'related_products',
          item_list_name: 'Related Products',
          item_name: curr.name,
          item_variant: '',
          location_id: this.$config.googleTagManager.googleApiLocationId,
          price: `${curr.price?.finalPrice}`,
        };
        curr.cat_names?.forEach((cat, index) => {
          const count = index + 1;
          if (count === 1) {
            item.item_category = cat;
          } else {
            item[`item_category${count}`] = cat;
          }
        });
        return [
          ...acc,
          item,
        ];
      }, []);
      window.gtag('event', 'view_item_list', {
        item_list_id: 'related_products',
        item_list_name: 'Related products',
        items: itemsRelatedProducts,
      });
    },
  },
};
